import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { BarChart3, PieChart, TrendingUp, Eye, Database, LineChart } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useState } from "react";
import ContactFormModal from "@/components/ContactFormModal";

export default function Analytics() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const features = [
    {
      icon: BarChart3,
      title: "Custom Dashboards",
      description: "Real-time dashboards tailored to your specific KPIs and metrics"
    },
    {
      icon: Database,
      title: "Data Integration",
      description: "Connect all your data sources into one unified view"
    },
    {
      icon: TrendingUp,
      title: "Predictive Analytics",
      description: "Forecast trends and identify opportunities before your competition"
    },
    {
      icon: Eye,
      title: "Visual Reports",
      description: "Beautiful, easy-to-understand reports that tell the story of your data"
    },
    {
      icon: LineChart,
      title: "Performance Tracking",
      description: "Monitor key metrics and get alerts when something needs attention"
    },
    {
      icon: PieChart,
      title: "Customer Insights",
      description: "Understand customer behavior and optimize your strategy"
    },
  ];

  const metrics = [
    { category: "Sales", examples: ["Revenue trends", "Conversion rates", "Sales pipeline", "Top products"] },
    { category: "Marketing", examples: ["Campaign ROI", "Lead sources", "Website traffic", "Engagement rates"] },
    { category: "Operations", examples: ["Efficiency metrics", "Resource utilization", "Process bottlenecks", "Cost analysis"] },
    { category: "Customer", examples: ["Satisfaction scores", "Retention rates", "Lifetime value", "Support tickets"] },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/20 to-background" />
          
          <div className="container relative z-10 max-w-4xl mx-auto text-center">
            <motion.h1
              className="text-5xl md:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="text-gradient">Business Analytics</span> That Drive Decisions
            </motion.h1>
            
            <motion.p
              className="text-xl text-muted-foreground mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Turn your data into actionable insights with custom analytics solutions built for your business.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Button
                size="lg"
                onClick={() => setIsContactOpen(true)}
                className="bg-gradient-to-r from-primary to-secondary text-white"
              >
                See Your Data Clearly
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 px-4">
          <div className="container max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Analytics Features
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  className="p-6 rounded-lg bg-card border border-border hover:border-primary/50 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <feature.icon className="w-12 h-12 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Metrics by Category */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Track What Matters
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {metrics.map((metric, index) => (
                <motion.div
                  key={metric.category}
                  className="p-6 rounded-lg bg-card border border-border"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <h3 className="text-2xl font-semibold mb-4">{metric.category}</h3>
                  <ul className="space-y-2">
                    {metric.examples.map((example) => (
                      <li key={example} className="flex items-center gap-2 text-muted-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-primary to-secondary" />
                        {example}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Why Analytics Matter
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { title: "Make Better Decisions", description: "Base strategy on data, not guesswork" },
                { title: "Identify Problems Early", description: "Catch issues before they become crises" },
                { title: "Find Growth Opportunities", description: "Discover untapped potential in your data" },
              ].map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  className="text-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Our Approach
            </h2>
            
            <div className="space-y-6">
              {[
                { step: "1", title: "Understand Your Goals", description: "What questions do you need answered? What decisions will this data inform?" },
                { step: "2", title: "Connect Your Data", description: "We integrate all your data sources into a unified analytics platform" },
                { step: "3", title: "Build Custom Dashboards", description: "Create visualizations that make complex data easy to understand" },
                { step: "4", title: "Train Your Team", description: "Ensure everyone knows how to use the analytics tools effectively" },
                { step: "5", title: "Iterate and Improve", description: "Continuously refine based on feedback and changing needs" },
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  className="flex gap-6 items-start"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Unlock Your Data?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Let's build analytics solutions that give you the insights you need to grow.
            </p>
            <Button
              size="lg"
              onClick={() => setIsContactOpen(true)}
              className="bg-gradient-to-r from-primary to-secondary text-white"
            >
              Get Started
            </Button>
          </div>
        </section>
      </div>
      <Footer />
      <ContactFormModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </>
  );
}
